// Costos y ventas del hato. Ganadería → Costos y ventas. Fase 2C (669).
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, DollarSign, Loader2, PawPrint, Plus, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { useCentrosContables } from '@/features/financiero/centroCosto';
import {
  BuscadorProducto, Location, ProductoOpt, locationsList, productosOptList,
} from '@/features/inventario/shared';
import {
  Animal, CATEGORIAS_COSTO, CategoriaCosto, Campo, Costo, Lote, Modal, PartnerOpt, Predio, Rentabilidad,
  SortTh, Switch, VentaResultado, categoriaCostoLabel, fieldCls, ganRpc, hoyISO, invalidateGanaderia,
  money, origenCostoLabel, partnersList, proyectosContablesList, useSort,
} from './shared';

type SubTab = 'rentabilidad' | 'costos' | 'venta';

export function Costos() {
  const [tab, setTab] = useState<SubTab>('rentabilidad');
  const [modalOpen, setModalOpen] = useState(false);
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const admin = isAdmin();

  const tabBtn = (t: SubTab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><DollarSign className="w-5 h-5" /> Costos y ventas</h1>
      <div className="flex flex-wrap gap-1 border-b pb-2">
        {tabBtn('rentabilidad', 'Rentabilidad')}
        {tabBtn('costos', 'Costos')}
        {tabBtn('venta', 'Venta de ganado')}
      </div>

      {tab === 'rentabilidad' && <RentabilidadTab />}
      {tab === 'costos' && <CostosTab admin={admin} onRegistrar={() => setModalOpen(true)} />}
      {tab === 'venta' && <VentaTab admin={admin} onVendido={() => invalidateGanaderia(qc)} />}

      {modalOpen && (
        <RegistrarCostoModal onClose={() => setModalOpen(false)} onSaved={() => invalidateGanaderia(qc)} />
      )}
    </div>
  );
}

// -------------------------------------------------------------- Rentabilidad
function RentabilidadTab() {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [expandido, setExpandido] = useState<string | null>(null);
  const { data, isFetching } = useQuery<Rentabilidad>({
    queryKey: ['gan-costo-rentabilidad', desde, hasta],
    queryFn: () => ganRpc<Rentabilidad>('gan_costo_rentabilidad', { _desde: desde || null, _hasta: hasta || null }),
    staleTime: 0, refetchOnMount: 'always',
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Desde" className="w-40"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta" className="w-40"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          {data && <span className="text-xs text-muted-foreground ml-auto">Ventana: {data.desde} → {data.hasta}</span>}
        </CardContent>
      </Card>

      {!data ? (
        <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Costo total</div><div className="text-2xl font-semibold font-mono tabular-nums mt-1">{money(data.totales.costo_total)}</div></CardContent></Card>
            <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Ingreso total</div><div className="text-2xl font-semibold font-mono tabular-nums mt-1">{money(data.totales.ingreso_total)}</div></CardContent></Card>
            <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Margen</div><div className={`text-2xl font-semibold font-mono tabular-nums mt-1 ${data.totales.margen < 0 ? 'text-urgente' : ''}`}>{money(data.totales.margen)}</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Rentabilidad por lote ({data.por_lote.length})</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Lote</th>
                  <th className="py-2 pr-3 text-right">Costo total</th><th className="py-2 pr-3 text-right">Ingreso total</th>
                  <th className="py-2 pr-3 text-right">Margen</th><th className="py-2"></th>
                </tr></thead>
                <tbody>
                  {data.por_lote.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin costos ni ventas en el período</td></tr>}
                  {data.por_lote.map((l, i) => {
                    const key = l.lote_id ?? `sin-lote-${i}`;
                    const abierto = expandido === key;
                    const categorias = Object.entries(l.costo_por_categoria || {});
                    return (
                      <Fragment key={key}>
                        <tr className={`border-b last:border-0 hover:bg-muted/40 cursor-pointer ${l.margen < 0 ? 'bg-urgente-soft/30' : ''}`}
                          onClick={() => setExpandido(abierto ? null : key)}>
                          <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                          <td className="py-2 pr-3 font-medium flex items-center gap-1">
                            {categorias.length > 0 && (abierto ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
                            {l.lote}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(l.costo_total)}</td>
                          <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(l.ingreso_total)}</td>
                          <td className={`py-2 pr-3 text-right font-mono tabular-nums font-semibold ${l.margen < 0 ? 'text-urgente' : 'text-estable'}`}>
                            {l.margen < 0 && <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}{money(l.margen)}
                          </td>
                          <td></td>
                        </tr>
                        {abierto && categorias.length > 0 && (
                          <tr className="border-b bg-muted/20">
                            <td></td>
                            <td colSpan={5} className="py-2 pr-3">
                              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                                {categorias.map(([cat, val]) => (
                                  <span key={cat}><span className="font-medium text-foreground">{categoriaCostoLabel(cat)}:</span> {money(Number(val))}</span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {data.por_lote.length > 0 && (
                    <tr className="border-t-2 font-semibold bg-muted/30">
                      <td className="py-2 pr-3" colSpan={2}>Totales ({data.por_lote.length})</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.totales.costo_total)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(data.totales.ingreso_total)}</td>
                      <td className={`py-2 pr-3 text-right font-mono tabular-nums ${data.totales.margen < 0 ? 'text-urgente' : ''}`}>{money(data.totales.margen)}</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------------- Costos
function CostosTab({ admin, onRegistrar }: { admin: boolean; onRegistrar: () => void }) {
  const qc = useQueryClient();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [loteId, setLoteId] = useState('');
  const [categoria, setCategoria] = useState('');

  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });
  const { data = [], isFetching } = useQuery<Costo[]>({
    queryKey: ['gan-costo-listar', desde, hasta, loteId, categoria],
    queryFn: () => ganRpc<Costo[]>('gan_costo_listar', {
      _desde: desde || null, _hasta: hasta || null, _lote: loteId || null, _categoria: categoria || null,
    }),
  });

  const contabilizar = useMutation({
    mutationFn: (id: string) => ganRpc<{ ok: boolean }>('gan_costo_contabilizar', { _id: id }),
    onSuccess: () => { toast.success('Costo contabilizado'); invalidateGanaderia(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = useCallback((r: Costo, key: string): string | number | null => {
    switch (key) {
      case 'fecha': return r.fecha;
      case 'categoria': return categoriaCostoLabel(r.categoria);
      case 'concepto': return r.concepto;
      case 'lote_nombre': return r.lote_nombre;
      case 'animal_arete': return r.animal_arete;
      case 'valor': return Number(r.valor);
      case 'origen': return origenCostoLabel(r.origen);
      default: return null;
    }
  }, []);
  const sort = useSort(data, getVal, 'fecha', 'desc');
  const totalValor = data.reduce((a, r) => a + Number(r.valor), 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Desde" className="w-40"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta" className="w-40"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label="Lote" className="w-48">
            <select className={fieldCls} value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              <option value="">Todos</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Categoría" className="w-48">
            <select className={fieldCls} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">Todas</option>
              {CATEGORIAS_COSTO.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </Campo>
          <Button className="ml-auto" disabled={!admin} onClick={onRegistrar}><Plus className="w-4 h-4 mr-1" /> Registrar costo</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Costos del hato ({data.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <SortTh col="categoria" sort={sort}>Categoría</SortTh>
              <SortTh col="concepto" sort={sort}>Concepto</SortTh>
              <SortTh col="lote_nombre" sort={sort}>Lote</SortTh>
              <SortTh col="animal_arete" sort={sort}>Animal</SortTh>
              <SortTh col="origen" sort={sort}>Origen</SortTh>
              <SortTh col="valor" sort={sort} align="right">Valor</SortTh>
              <th className="py-2 pr-3">Contable</th>
              <th className="py-2"></th>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin costos registrados</td></tr>}
              {sort.sorted.map((c, i) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{c.fecha}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{categoriaCostoLabel(c.categoria)}</td>
                  <td className="py-2 pr-3">{c.concepto}{c.referencia_inventario ? <span className="text-xs text-muted-foreground"> ({c.referencia_inventario})</span> : ''}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.lote_nombre ?? '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.animal_arete ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">{origenCostoLabel(c.origen)}</span>
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.valor)}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{c.asiento_id ? 'Contabilizado' : '—'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {!c.asiento_id && (
                      <Button size="sm" variant="outline" disabled={!admin} isLoading={contabilizar.isPending}
                        onClick={() => contabilizar.mutate(c.id)}>
                        Contabilizar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {sort.sorted.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={7}>Totales ({sort.sorted.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totalValor)}</td>
                  <td colSpan={2}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// -------------------------------------------------------- Registrar costo ---
function RegistrarCostoModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [categoria, setCategoria] = useState<CategoriaCosto>('alimentacion');
  const [concepto, setConcepto] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [loteId, setLoteId] = useState('');
  const [predioId, setPredioId] = useState('');
  const [animalId, setAnimalId] = useState('');
  const [valor, setValor] = useState('');
  const [descontarInventario, setDescontarInventario] = useState(false);
  const [productoId, setProductoId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [centroCostoId, setCentroCostoId] = useState('');
  const [proyectoId, setProyectoId] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });
  const { data: predios = [] } = useQuery<Predio[]>({ queryKey: ['gan-predios'], queryFn: () => ganRpc<Predio[]>('gan_predio_listar') });
  const { data: animales = [] } = useQuery<Animal[]>({ queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar') });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList, enabled: descontarInventario });
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList, enabled: descontarInventario });
  const { data: centros = [] } = useCentrosContables();
  const { data: proyectos = [] } = useQuery({ queryKey: ['cont-proyectos'], queryFn: proyectosContablesList });

  const registrar = useMutation({
    mutationFn: () => {
      if (!concepto.trim()) throw new Error('Ingresa el concepto del costo');
      if (descontarInventario) {
        if (!productoId) throw new Error('Selecciona el producto (insumo) a descontar');
        if (!locationId) throw new Error('Selecciona la bodega');
        if (!(Number(cantidad) > 0)) throw new Error('Ingresa la cantidad a descontar');
      } else if (!(Number(valor) >= 0)) {
        throw new Error('Ingresa el valor del costo');
      }
      return ganRpc('gan_costo_registrar', {
        _data: {
          categoria, concepto, fecha,
          lote_id: loteId || null, predio_id: predioId || null, animal_id: animalId || null,
          centro_costo_id: centroCostoId || null, proyecto_id: proyectoId || null,
          observaciones: observaciones || null,
          ...(descontarInventario
            ? { producto_id: productoId, location_id: locationId, cantidad: Number(cantidad) }
            : { valor: Number(valor) }),
        },
      });
    },
    onSuccess: () => { toast.success('Costo registrado'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = concepto.trim() !== '' && (descontarInventario ? (!!productoId && !!locationId && Number(cantidad) > 0) : Number(valor) >= 0);

  return (
    <Modal title="Registrar costo" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Categoría *">
            <select className={fieldCls} value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaCosto)}>
              {CATEGORIAS_COSTO.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </Campo>
          <Campo label="Concepto *" className="col-span-2"><input className={fieldCls} value={concepto} onChange={(e) => setConcepto(e.target.value)} /></Campo>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="Lote">
            <select className={fieldCls} value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Predio">
            <select className={fieldCls} value={predioId} onChange={(e) => setPredioId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {predios.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Animal">
            <select className={fieldCls} value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {animales.map((a) => <option key={a.id} value={a.id}>{a.arete}{a.nombre ? ` · ${a.nombre}` : ''}</option>)}
            </select>
          </Campo>
        </div>

        <div className="rounded-md border p-3 space-y-3">
          <Switch checked={descontarInventario} onChange={setDescontarInventario} label="Descontar de inventario (consumo de un insumo)" />
          {descontarInventario ? (
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Producto (insumo) *" className="col-span-1">
                <BuscadorProducto value={productoId} onChange={setProductoId} productos={productos} compact />
              </Campo>
              <Campo label="Bodega *">
                <select className={fieldCls} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Cantidad *"><input type="number" min="0" step="0.001" className={fieldCls} value={cantidad} onChange={(e) => setCantidad(e.target.value)} /></Campo>
              <p className="col-span-3 text-xs text-muted-foreground">El valor se calcula al costo promedio del insumo en esa bodega y descuenta el stock (kardex).</p>
            </div>
          ) : (
            <Campo label="Valor ($) *" className="max-w-xs"><input type="number" min="0" step="0.01" className={fieldCls} value={valor} onChange={(e) => setValor(e.target.value)} /></Campo>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Campo label="Centro de costo (opcional, para contabilizar)">
            <select className={fieldCls} value={centroCostoId} onChange={(e) => setCentroCostoId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Proyecto (opcional)">
            <select className={fieldCls} value={proyectoId} onChange={(e) => setProyectoId(e.target.value)}>
              <option value="">— Ninguno —</option>
              {proyectos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
            </select>
          </Campo>
        </div>
        <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Campo>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={!puedeGuardar} onClick={() => registrar.mutate()}>Registrar</Button>
        </div>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------- Venta de ganado
interface AnimalVenta { animal_id: string; precio: string; peso_kg: string; descripcion: string }

function VentaTab({ admin, onVendido }: { admin: boolean; onVendido: () => void }) {
  const [clienteId, setClienteId] = useState('');
  const [centroCostoId, setCentroCostoId] = useState('');
  const [establecimiento, setEstablecimiento] = useState('001');
  const [puntoEmision, setPuntoEmision] = useState('001');
  const [formaPago, setFormaPago] = useState('01');
  const [plazoDias, setPlazoDias] = useState('0');
  const [ivaTarifa, setIvaTarifa] = useState('0');
  const [emitir, setEmitir] = useState(true);
  const [observaciones, setObservaciones] = useState('');
  const [seleccion, setSeleccion] = useState<Record<string, AnimalVenta>>({});
  const [resultado, setResultado] = useState<VentaResultado | null>(null);

  const { data: animales = [], isFetching } = useQuery<Animal[]>({
    queryKey: ['gan-animales-activos-venta'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar', { _estado: 'activo' }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const clientes = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('cliente')), [partners]);
  const { data: centros = [] } = useCentrosContables();

  const toggleAnimal = (id: string) => setSeleccion((s) => {
    const n = { ...s };
    if (n[id]) delete n[id]; else n[id] = { animal_id: id, precio: '', peso_kg: '', descripcion: '' };
    return n;
  });
  const setCampo = (id: string, patch: Partial<AnimalVenta>) =>
    setSeleccion((s) => (s[id] ? { ...s, [id]: { ...s[id], ...patch } } : s));

  const elegidos = Object.values(seleccion);
  const totalPreview = elegidos.reduce((a, s) => a + (Number(s.precio) || 0), 0) * (1 + Number(ivaTarifa || 0) / 100);

  const vender = useMutation({
    mutationFn: () => {
      if (!clienteId) throw new Error('Selecciona el cliente');
      if (!centroCostoId) throw new Error('Selecciona el centro de costo (lo exige la facturación SRI)');
      if (elegidos.length === 0) throw new Error('Selecciona al menos un animal a vender');
      const invalido = elegidos.find((e) => !(Number(e.precio) > 0));
      if (invalido) throw new Error('Todos los animales seleccionados deben tener un precio válido');
      return ganRpc<VentaResultado>('gan_venta_registrar', {
        _data: {
          cliente_partner_id: clienteId,
          centro_costo_id: centroCostoId,
          animales: elegidos.map((e) => ({
            animal_id: e.animal_id, precio: Number(e.precio),
            peso_kg: e.peso_kg === '' ? null : Number(e.peso_kg),
            descripcion: e.descripcion || null,
          })),
          establecimiento, punto_emision: puntoEmision, forma_pago: formaPago,
          plazo_dias: Number(plazoDias) || 0, observaciones: observaciones || null,
          emitir, iva_tarifa: Number(ivaTarifa) || 0,
        },
      });
    },
    onSuccess: (r) => {
      setResultado(r);
      toast.success(r.message || 'Venta registrada');
      setSeleccion({});
      onVendido();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Datos de la venta</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Cliente (RUC o razón social) *</label>
            <select className={fieldCls} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
            </select>
          </div>
          <Campo label="Centro de costo *">
            <select className={fieldCls} value={centroCostoId} onChange={(e) => setCentroCostoId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Establecimiento"><input className={fieldCls} value={establecimiento} onChange={(e) => setEstablecimiento(e.target.value)} /></Campo>
          <Campo label="Punto de emisión"><input className={fieldCls} value={puntoEmision} onChange={(e) => setPuntoEmision(e.target.value)} /></Campo>
          <Campo label="Forma de pago (código SRI)"><input className={fieldCls} value={formaPago} onChange={(e) => setFormaPago(e.target.value)} /></Campo>
          <Campo label="Plazo (días)"><input type="number" min="0" className={fieldCls} value={plazoDias} onChange={(e) => setPlazoDias(e.target.value)} /></Campo>
          <Campo label="IVA (%)"><input type="number" min="0" step="0.01" className={fieldCls} value={ivaTarifa} onChange={(e) => setIvaTarifa(e.target.value)} /></Campo>
          <div className="flex items-end pb-2">
            <Switch checked={emitir} onChange={setEmitir} label="Emitir factura SRI" />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Observaciones</label>
            <input className={fieldCls} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PawPrint className="w-4 h-4" /> Animales a vender ({elegidos.length} seleccionado{elegidos.length === 1 ? '' : 's'})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 w-8"></th><th className="py-2 pr-3 text-right">N°</th>
              <th className="py-2 pr-3">Arete</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Lote</th>
              <th className="py-2 pr-3 text-right w-32">Precio ($)</th><th className="py-2 pr-3 text-right w-28">Peso (kg)</th>
              <th className="py-2 pr-3">Descripción</th>
            </tr></thead>
            <tbody>
              {animales.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No hay animales activos</td></tr>}
              {animales.map((a, i) => {
                const sel = seleccion[a.id];
                return (
                  <tr key={a.id} className={`border-b last:border-0 ${sel ? 'bg-primary/5' : ''}`}>
                    <td className="py-1 pr-3"><input type="checkbox" checked={!!sel} onChange={() => toggleAnimal(a.id)} disabled={!admin} /></td>
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{a.arete}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.nombre ?? '—'}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.lote_nombre ?? '—'}</td>
                    <td className="py-1 pr-3">
                      <input type="number" min="0" step="0.01" className={`${fieldCls} h-8 text-right`} disabled={!sel}
                        value={sel?.precio ?? ''} onChange={(e) => setCampo(a.id, { precio: e.target.value })} />
                    </td>
                    <td className="py-1 pr-3">
                      <input type="number" min="0" step="0.1" className={`${fieldCls} h-8 text-right`} disabled={!sel}
                        value={sel?.peso_kg ?? ''} onChange={(e) => setCampo(a.id, { peso_kg: e.target.value })} />
                    </td>
                    <td className="py-1 pr-3">
                      <input className={`${fieldCls} h-8`} disabled={!sel} placeholder={`Semoviente ${a.arete}`}
                        value={sel?.descripcion ?? ''} onChange={(e) => setCampo(a.id, { descripcion: e.target.value })} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between rounded-md border p-3 bg-muted/20">
        <p className="text-xs text-muted-foreground max-w-xl">
          {emitir
            ? 'La factura se encola para emisión al SRI (requiere certificado de firma y tipo de cambio configurados en Financiero).'
            : 'Se registrará la venta y se marcarán los animales como vendidos, sin emitir factura.'}
        </p>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Total estimado</div>
          <div className="text-xl font-semibold font-mono tabular-nums">{money(totalPreview)}</div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button disabled={!admin || elegidos.length === 0} isLoading={vender.isPending} onClick={() => vender.mutate()}>
          <Receipt className="w-4 h-4 mr-1" /> Registrar venta
        </Button>
      </div>

      {resultado && (
        <Card className="border-estable">
          <CardHeader className="pb-2"><CardTitle className="text-base">Resultado de la venta</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p><span className="text-muted-foreground">Animales vendidos:</span> <span className="font-mono tabular-nums">{resultado.vendidos}</span></p>
            {resultado.ticket_id && <p><span className="text-muted-foreground">Ticket:</span> {resultado.ticket_id}</p>}
            {resultado.importe_total != null && <p><span className="text-muted-foreground">Importe total:</span> {money(resultado.importe_total)}</p>}
            <p className="text-muted-foreground">{resultado.message}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
